import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { Loader2, ChevronLeft, ChevronRight, MapPin, CheckSquare } from 'lucide-react';
import { toast } from 'sonner';
import ConstructionContextSelector from './ConstructionContextSelector';
import ConstructionBreadcrumb from './ConstructionBreadcrumb';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const PAGE = 25;

// Admin studio: bulk-map ConstructionContext onto activities. Individual BOQ/drawing mapping
// happens in the Activity Detail drawer (opened by clicking the activity name).
const ActivityMappingStudio = ({ projectId, scheduleId, onOpenActivity }) => {
  const [data, setData] = useState({ items: [], total: 0 });
  const [page, setPage] = useState(1);
  const [unmappedOnly, setUnmappedOnly] = useState(true);
  const [selected, setSelected] = useState({});
  const [bulkCtx, setBulkCtx] = useState({});
  const [loading, setLoading] = useState(true);
  const [applying, setApplying] = useState(false);

  const load = useCallback(async () => {
    if (!scheduleId) { setLoading(false); return; }
    setLoading(true);
    try {
      const params = { page, pageSize: PAGE };
      if (unmappedOnly) params.contextMapped = false;
      const d = await axios.get(`${API}/projects/${projectId}/schedules/${scheduleId}/activities`, { params }).then((r) => r.data);
      setData(d);
    } catch (e) { toast.error('Failed to load activities'); } finally { setLoading(false); }
  }, [projectId, scheduleId, page, unmappedOnly]);

  useEffect(() => { load(); }, [load]);

  const selectedIds = Object.keys(selected).filter((k) => selected[k]);
  const toggle = (id) => setSelected((s) => ({ ...s, [id]: !s[id] }));
  const allOnPage = data.items.length > 0 && data.items.every((a) => selected[a.id]);
  const toggleAll = () => {
    const next = { ...selected };
    if (allOnPage) data.items.forEach((a) => { next[a.id] = false; });
    else data.items.forEach((a) => { next[a.id] = true; });
    setSelected(next);
  };

  const applyBulk = async () => {
    if (selectedIds.length === 0) { toast.error('Select activities first'); return; }
    if (Object.keys(bulkCtx).length === 0) { toast.error('Pick a context to apply'); return; }
    setApplying(true);
    try {
      const r = await axios.post(`${API}/projects/${projectId}/activities/bulk-context`, { activityIds: selectedIds, context: bulkCtx });
      toast.success(`Mapped ${r.data.updated} activities`);
      setSelected({}); await load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Bulk mapping failed'); } finally { setApplying(false); }
  };

  const totalPages = Math.max(1, Math.ceil((data.total || 0) / PAGE));

  return (
    <div className="space-y-4" data-testid="mapping-studio">
      <div className="flex items-center gap-3 flex-wrap">
        <label className="flex items-center gap-2 text-sm cursor-pointer" data-testid="mapping-unmapped-label">
          <input type="checkbox" checked={unmappedOnly} onChange={(e) => { setUnmappedOnly(e.target.checked); setPage(1); }} className="w-4 h-4 accent-blue-600" data-testid="mapping-unmapped-toggle" />
          Unmapped only
        </label>
        <span className="text-xs text-slate-400">{selectedIds.length} selected</span>
      </div>

      <div className="bg-white rounded-xl border border-slate-200 p-4 space-y-3" data-testid="bulk-map-panel">
        <h4 className="text-sm font-semibold text-slate-800 flex items-center gap-2"><MapPin className="w-4 h-4" /> Bulk map ConstructionContext to selected</h4>
        <ConstructionContextSelector projectId={projectId} value={bulkCtx} onChange={setBulkCtx} />
        <button onClick={applyBulk} disabled={applying || selectedIds.length === 0} className="px-4 py-2 bg-blue-600 text-white rounded-lg text-sm flex items-center gap-2 disabled:opacity-50" data-testid="bulk-apply-btn">
          {applying ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckSquare className="w-4 h-4" />} Apply to {selectedIds.length} selected
        </button>
      </div>

      <div className="bg-white rounded-xl border border-slate-200 overflow-x-auto" data-testid="mapping-table">
        <table className="w-full text-sm">
          <thead><tr className="text-left text-slate-500 border-b border-slate-100">
            <th className="px-3 py-2"><input type="checkbox" checked={allOnPage} onChange={toggleAll} className="w-4 h-4 accent-blue-600" data-testid="mapping-select-all" /></th>
            <th className="px-4 py-2">Activity</th><th className="px-4 py-2">Current Context</th></tr></thead>
          <tbody>
            {loading ? (
              <tr><td colSpan="3" className="px-4 py-10 text-center text-slate-400"><Loader2 className="w-5 h-5 animate-spin inline" /></td></tr>
            ) : data.items.length === 0 ? (
              <tr><td colSpan="3" className="px-4 py-8 text-center text-slate-400" data-testid="mapping-empty">{unmappedOnly ? 'All activities are mapped 🎉' : 'No activities.'}</td></tr>
            ) : data.items.map((a) => (
              <tr key={a.id} className="border-b border-slate-50" data-testid="mapping-row">
                <td className="px-3 py-3"><input type="checkbox" checked={!!selected[a.id]} onChange={() => toggle(a.id)} className="w-4 h-4 accent-blue-600" data-testid={`mapping-select-${a.id}`} /></td>
                <td className="px-4 py-3"><button onClick={() => onOpenActivity(a.id)} className="text-left text-slate-800 hover:text-blue-600 font-medium" data-testid="mapping-open-activity">{a.activityCode ? `${a.activityCode} · ` : ''}{a.activityName}</button></td>
                <td className="px-4 py-3"><ConstructionBreadcrumb projectId={projectId} context={a.constructionContext} fallback="Unmapped" testId={`map-ctx-${a.id}`} /></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="flex items-center justify-between text-sm text-slate-500">
        <span>{data.total} activities</span>
        <div className="flex items-center gap-2">
          <button disabled={page <= 1} onClick={() => setPage((p) => p - 1)} className="p-1 disabled:opacity-30" data-testid="mapping-prev-page"><ChevronLeft className="w-4 h-4" /></button>
          <span>Page {page} / {totalPages}</span>
          <button disabled={page >= totalPages} onClick={() => setPage((p) => p + 1)} className="p-1 disabled:opacity-30" data-testid="mapping-next-page"><ChevronRight className="w-4 h-4" /></button>
        </div>
      </div>
    </div>
  );
};

export default ActivityMappingStudio;
